import React, { useEffect } from 'react';
import { Flex } from '@televet/kibble-ui/build/chakra';
import { GetSidePanelPetParentProfileQuery, useGetSidePanelPetParentProfileQuery } from 'shared/types/graphql';
import MergeProfilesProfileCard from './MergeProfilesProfileCard';
import { GraphQLFetchPolicies } from 'shared/enums/GraphQLFetchPolicies';
import { getClinicPetParentDisplayName } from 'shared/utils';
import { useAppDispatch, useAppSelector } from 'state/hooks';
import {
  MergeProfilesPet,
  setPimsProfile,
  setSecondProfileIsPrimary,
  updateMergeProfilesResult,
} from 'shared/components/MergeProfiles/state/mergeProfilesSlice';
import { isNumberVerifiedMobile } from '../utils';

interface IMergeProfilesSelectInfoProps {
  currentClientProfile: GetSidePanelPetParentProfileQuery['clinicPetParent'];
  clinicId: string | undefined;
  selectedMergeProfileId: string;
}

const MergeProfilesSelectInfo = ({
  currentClientProfile,
  clinicId,
  selectedMergeProfileId,
}: IMergeProfilesSelectInfoProps): JSX.Element => {
  const dispatch = useAppDispatch();
  const resultName = useAppSelector((state) => state.mergeProfiles.mergeProfileResult.name);
  const secondProfileIsPrimary = useAppSelector((state) => state.mergeProfiles.secondProfileIsPrimary);

  const { data: petParentData } = useGetSidePanelPetParentProfileQuery({
    variables: { clinicPetParentId: selectedMergeProfileId || '', clinicId: clinicId || '' },
    skip: !selectedMergeProfileId || !clinicId,
    fetchPolicy: GraphQLFetchPolicies.CacheAndNetwork,
  });

  useEffect(() => {
    if (!!petParentData?.clinicPetParent?.pimsId || !!currentClientProfile?.pimsId) {
      dispatch(setPimsProfile(true));
    } else {
      dispatch(setPimsProfile(false));
    }
  }, [currentClientProfile?.pimsId, dispatch, petParentData?.clinicPetParent?.pimsId]);

  const getMergePetsFromProfile = (
    profile: GetSidePanelPetParentProfileQuery['clinicPetParent'],
  ): MergeProfilesPet[] => {
    return (
      profile?.pets?.map((pet) => {
        return {
          id: pet.id,
          name: pet.name,
        };
      }) || []
    );
  };

  // set initial mergeResult state based on primary profile
  useEffect(() => {
    // don't reset if there's already a result stored or if still loading second profile
    if (resultName || !petParentData) return;

    // first is primary unless second has pimsId and first does not
    const secondIsPrimary = !currentClientProfile?.pimsId && !!petParentData?.clinicPetParent?.pimsId;
    dispatch(setSecondProfileIsPrimary(secondIsPrimary));

    const profile = secondIsPrimary ? petParentData?.clinicPetParent : currentClientProfile;

    const combinedPets = [
      ...getMergePetsFromProfile(currentClientProfile),
      ...getMergePetsFromProfile(petParentData?.clinicPetParent),
    ];

    dispatch(
      updateMergeProfilesResult({
        name: getClinicPetParentDisplayName({
          firstName: profile?.firstName,
          lastName: profile?.lastName,
        }),
        email: profile?.email || '',
        phoneNumbers: [
          ...(currentClientProfile?.phoneNumbers
            ?.filter((number) => !number.isDeleted)
            .map((number) => {
              return {
                id: number.id,
                number: number.number,
                isPrimary: !!number.isPrimary,
                isSelected: !secondIsPrimary,
                verifiedMobile: isNumberVerifiedMobile(number.phoneNumberLookupResult),
              };
            }) || []),
          ...(petParentData?.clinicPetParent?.phoneNumbers
            ?.filter((number) => !number.isDeleted)
            .map((number) => {
              return {
                id: number.id,
                number: number.number,
                isPrimary: !!number.isPrimary,
                isSelected: secondIsPrimary,
                verifiedMobile: isNumberVerifiedMobile(number.phoneNumberLookupResult),
              };
            }) || []),
        ],
        sendSurvey: true,
        contactPreferences: {
          email: true,
          sms: true,
        },
        pets: combinedPets,
      }),
    );
  }, [currentClientProfile, dispatch, resultName, petParentData]);

  return (
    <Flex gap={6}>
      <MergeProfilesProfileCard
        cardTitle="Profile 1"
        clientProfile={currentClientProfile}
        isPrimaryProfile={!secondProfileIsPrimary}
      />
      <MergeProfilesProfileCard
        cardTitle="Profile 2"
        clientProfile={petParentData?.clinicPetParent}
        isPrimaryProfile={secondProfileIsPrimary}
      />
      <MergeProfilesProfileCard
        cardTitle="Merged Profile"
        clientProfile={secondProfileIsPrimary ? petParentData?.clinicPetParent : currentClientProfile}
        isMergeResult
      />
    </Flex>
  );
};

export default MergeProfilesSelectInfo;
